import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ZoomableImage.css";

export default function ZoomableImage({
  // Multiple images:
  // [{ path, altText, title (or name), description, lightboxTitle?, lightboxDescription? }, ...]
  images,

  // Single image
  path,
  altText,
  name,
  title,
  description,
  lightboxTitle,
  lightboxDescription,

  location = "left",    // "right" | "left" | "centered"
  wrap = true,          // false = sits alone on its own row
  width,                // defaults: 340px (single), "100%" (grid)
  spacing = "1.75rem",  // space between this and surrounding content
  padding = 10,         // padding around each image inside its card

  // Caption settings
  lightboxCaption = true, // show title/description on the enlarged image
  cardCaption = true,     // show title/description under the thumbnail

  // Grid-only settings
  columns,              // fixed number of columns, e.g. 3 (default: auto-fit)
  minColumnWidth = 220, // min card width when columns is auto
  imageHeight = 220,    // image area height for each grid card
  gap = 16,             // space between grid cards

  className = "",
  style,
}) {
  const items = images?.length
    ? images
    : [{ path, altText, name, title, description, lightboxTitle, lightboxDescription }];
  const count = items.length;
  const isGrid = count > 1;

  const [openIndex, setOpenIndex] = useState(null);
  const [direction, setDirection] = useState(null); // "left" | "right" | null
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const isOpen = openIndex !== null;

  const triggerRefs = useRef([]);
  const closeButtonRef = useRef(null);
  const lastIndexRef = useRef(0);

  // Swipe tracking
  const gestureRef = useRef({
    active: false,
    dragging: false,
    startX: 0,
    startY: 0,
    startTime: 0,
    pointerId: null,
  });
  const didDragRef = useRef(false);

  const toCss = (value) => (typeof value === "number" ? `${value}px` : value);

  // Text helpers
  const getTitle = (item) => item?.title ?? item?.name;
  const getLightboxTitle = (item) => item?.lightboxTitle ?? getTitle(item);
  const getLightboxDescription = (item) =>
    item?.lightboxDescription ?? item?.description;

  const open = (index) => {
    setDirection(null);
    setDragX(0);
    setOpenIndex(index);
  };

  const close = () => {
    setOpenIndex(null);
    setDragX(0);
    setIsDragging(false);
  };

  const showPrev = () => {
    setDirection("left");
    setOpenIndex((i) => (i - 1 + count) % count);
  };

  const showNext = () => {
    setDirection("right");
    setOpenIndex((i) => (i + 1) % count);
  };

  // Remember which image was last viewed (so focus returns to it on close)
  useEffect(() => {
    if (openIndex !== null) lastIndexRef.current = openIndex;
  }, [openIndex]);

  // Scroll lock, keyboard controls, focus management
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpenIndex(null);
      } else if (count > 1 && e.key === "ArrowLeft") {
        setDirection("left");
        setOpenIndex((i) => (i - 1 + count) % count);
      } else if (count > 1 && e.key === "ArrowRight") {
        setDirection("right");
        setOpenIndex((i) => (i + 1) % count);
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    closeButtonRef.current?.focus();
    const triggers = triggerRefs.current;

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      triggers[lastIndexRef.current]?.focus();
    };
  }, [isOpen, count]);

  /* ---------- Swipe handlers (mouse + touch + pen) ---------- */

  const handlePointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return; // left click only
    if (e.target.closest("button")) return; // don't hijack close/arrow buttons

    didDragRef.current = false;
    gestureRef.current = {
      active: true,
      dragging: false,
      startX: e.clientX,
      startY: e.clientY,
      startTime: performance.now(),
      pointerId: e.pointerId,
    };
  };

  const handlePointerMove = (e) => {
    const g = gestureRef.current;
    if (!g.active || e.pointerId !== g.pointerId) return;

    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;

    if (!g.dragging) {
      if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
      g.dragging = true;
      didDragRef.current = true;
      setIsDragging(true);
      e.currentTarget.setPointerCapture(e.pointerId);
    }

    setDragX(dx);
  };

  const endGesture = (e, cancelled = false) => {
    const g = gestureRef.current;
    if (!g.active || e.pointerId !== g.pointerId) return;
    g.active = false;

    if (!g.dragging) return;

    if (!cancelled) {
      const dx = e.clientX - g.startX;
      const elapsed = Math.max(performance.now() - g.startTime, 1);
      const velocity = Math.abs(dx) / elapsed; // px per ms
      const threshold = Math.min(120, window.innerWidth * 0.2);
      const isFlick = Math.abs(dx) > 30 && velocity > 0.5;

      if (dx <= -threshold || (isFlick && dx < 0)) showNext();
      else if (dx >= threshold || (isFlick && dx > 0)) showPrev();
    }

    setDragX(0);
    setIsDragging(false);
  };

  const handleBackdropClick = () => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    close();
  };

  /* ---------- Classes & styles ---------- */

  const positionClass =
    {
      right: "zoomable--right",
      left: "zoomable--left",
      centered: "zoomable--centered",
    }[location] ?? "zoomable--left";

  const wrapClass = wrap ? "" : "zoomable--no-wrap";
  const rootClass = `zoomable ${positionClass} ${wrapClass} ${className}`;

  const wrapperStyle = {
    "--zi-width": toCss(width ?? (isGrid ? "100%" : 340)),
    "--zi-spacing": toCss(spacing),
    "--zi-padding": toCss(padding),
    "--zi-gap": toCss(gap),
    "--zi-image-height": toCss(imageHeight),
    "--zi-columns": columns
      ? `repeat(auto-fill, minmax(min(100%, max(${toCss(minColumnWidth)}, calc((100% - ${
          columns - 1
        } * ${toCss(gap)}) / ${columns}))), 1fr))`
      : `repeat(auto-fit, minmax(min(${toCss(minColumnWidth)}, 100%), 1fr))`,
    ...style,
  };

  const slideStyle = {
    transform: `translateX(${dragX}px)`,
    opacity:
      typeof window !== "undefined"
        ? 1 - Math.min(Math.abs(dragX) / window.innerWidth, 1) * 0.6
        : 1,
    transition: isDragging ? "none" : "transform 0.25s ease, opacity 0.25s ease",
  };

  const renderCardContent = (item, index) => {
    const itemTitle = getTitle(item);
    return (
      <>
        <button
          ref={(el) => (triggerRefs.current[index] = el)}
          type="button"
          className="zoomable__trigger"
          onClick={() => open(index)}
          aria-label={`Enlarge image: ${itemTitle || item.altText}`}
        >
          <img src={item.path} alt={item.altText} loading="lazy" />
        </button>

        {cardCaption && (itemTitle || item.description) && (
          <figcaption className="zoomable__caption">
            {itemTitle && <h3>{itemTitle}</h3>}
            {item.description && <p>{item.description}</p>}
          </figcaption>
        )}
      </>
    );
  };

  const current = isOpen ? items[openIndex] : null;
  const currentTitle = getLightboxTitle(current);
  const currentDescription = getLightboxDescription(current);
  const showCaption = Boolean(lightboxCaption && (currentTitle || currentDescription));

  const frameClass = [
    "lightbox-image-container",
    direction ? `lightbox-image-container--from-${direction}` : "",
    showCaption ? "lightbox-image-container--has-caption" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      {isGrid ? (
        <div className={`${rootClass} zoomable--grid`} style={wrapperStyle}>
          {items.map((item, index) => (
            <figure className="zoomable__card" key={item.path ?? index}>
              {renderCardContent(item, index)}
            </figure>
          ))}
        </div>
      ) : (
        <figure className={`${rootClass} zoomable__card`} style={wrapperStyle}>
          {renderCardContent(items[0], 0)}
        </figure>
      )}

      {isOpen &&
        createPortal(
          <div
            className="image-lightbox"
            onClick={handleBackdropClick}
            onPointerDown={isGrid ? handlePointerDown : undefined}
            onPointerMove={isGrid ? handlePointerMove : undefined}
            onPointerUp={isGrid ? (e) => endGesture(e) : undefined}
            onPointerCancel={isGrid ? (e) => endGesture(e, true) : undefined}
            role="dialog"
            aria-modal="true"
            aria-label={currentTitle ?? current.altText}
          >
            <button
              ref={closeButtonRef}
              className="lightbox-close"
              onClick={close}
              aria-label="Close image"
            >
              ×
            </button>

            {isGrid && (
              <>
                <button
                  className="lightbox-nav lightbox-nav--prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    showPrev();
                  }}
                  aria-label="Previous image"
                >
                  ‹
                </button>
                <button
                  className="lightbox-nav lightbox-nav--next"
                  onClick={(e) => {
                    e.stopPropagation();
                    showNext();
                  }}
                  aria-label="Next image"
                >
                  ›
                </button>
                <p className="lightbox-counter">
                  {openIndex + 1} / {count}
                </p>
              </>
            )}

            {/* Follows your finger/mouse; remounts per image */}
            <div
              key={openIndex}
              className={`lightbox-slide ${isGrid ? "lightbox-slide--swipeable" : ""} ${
                isDragging ? "is-dragging" : ""
              }`}
              style={slideStyle}
            >
              {/* The frame: padding + translucent background + border.
                  The image sits directly inside it, inset by the padding. */}
              <figure className={frameClass} onClick={(e) => e.stopPropagation()}>
                <img
                  src={current.path}
                  alt={current.altText}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                />

                {showCaption && (
                  <figcaption className="lightbox-caption">
                    {currentTitle && (
                      <h2 className="lightbox-caption__title">{currentTitle}</h2>
                    )}
                    {currentDescription && (
                      <p className="lightbox-caption__description">
                        {currentDescription}
                      </p>
                    )}
                  </figcaption>
                )}
              </figure>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}